import {
  ChatAlt2Icon,
  ChipIcon,
  CollectionIcon,
  DocumentTextIcon,
  LightningBoltIcon,
  MicrophoneIcon,
  PhotographIcon,
  TemplateIcon,
} from '@heroicons/react/outline';
import type { ComponentType, SVGProps } from 'react';

import clsxm from '@/lib/clsxm';

import { SectionHeading, Stage, tone } from '@/components/agents/primitives';

import { Avatar, COMPANY, TEAM, type Teammate, useB2bTranslation } from './kit';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

const card =
  'rounded-[22px] bg-white text-start shadow-[0_30px_70px_-35px_rgba(22,12,90,0.45)] ring-1 ring-black/5 dark:bg-[#16151f] dark:ring-white/10';

export default function TeamsSharing() {
  const { t } = useB2bTranslation();

  const shareable: { icon: Icon; label: string }[] = [
    { icon: ChatAlt2Icon, label: t('Chats') },
    { icon: LightningBoltIcon, label: t('Prompts') },
    { icon: MicrophoneIcon, label: t('Brand voices') },
    { icon: TemplateIcon, label: t('Custom tools') },
    { icon: ChipIcon, label: t('AI agents') },
    { icon: PhotographIcon, label: t('Images and videos') },
    { icon: DocumentTextIcon, label: t('Transcriptions') },
    { icon: CollectionIcon, label: t('Workflows') },
  ];

  return (
    <section id='teamwork' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('Share the work.')}
          tail={t('Not the password.')}
          lead={t(
            'Everyone gets their own login and their own allowance. Share chats, prompts, brand voices, custom tools and agents with the whole team in one click.',
          )}
        />
      </div>

      <div className='mx-auto mt-14 max-w-7xl px-3 sm:px-6 lg:px-8'>
        <Stage
          variant='lavender'
          halftoneAt='100% 100%'
          className='px-4 py-8 sm:px-10 sm:py-12'
        >
          <div
            aria-hidden='true'
            className='grid gap-5 lg:grid-cols-3 lg:items-start'
          >
            <div>
              <ShareDialog />
            </div>
            <div className='lg:mt-10'>
              <PromptLibrary />
            </div>
            <div>
              <BrandVoice />
            </div>
          </div>
        </Stage>
      </div>

      <div className='mx-auto mt-12 max-w-7xl px-6 lg:px-8'>
        <p className={clsxm('text-sm font-semibold', tone.ink)}>
          {t('What your team can share')}
        </p>
        <ul className='mt-5 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4'>
          {shareable.map(({ icon: ItemIcon, label }) => (
            <li
              key={label}
              className={clsxm(
                'flex items-center gap-3 text-[15px]',
                tone.body,
              )}
            >
              <span className='flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-white text-primary-600 ring-1 ring-[#0f0f28]/[0.07] dark:bg-white/[0.05] dark:text-primary-300 dark:ring-white/10'>
                <ItemIcon className='h-[18px] w-[18px]' />
              </span>
              {label}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Toggle({ on }: { on: boolean }) {
  return (
    <span
      className={clsxm(
        'relative inline-flex h-5 w-9 flex-none items-center rounded-full',
        on ? 'bg-primary-600' : 'bg-[#dcdce6] dark:bg-white/15',
      )}
    >
      <span
        className={clsxm(
          'absolute h-4 w-4 rounded-full bg-white shadow',
          on ? 'end-0.5' : 'start-0.5',
        )}
      />
    </span>
  );
}

function ShareDialog() {
  const { t } = useB2bTranslation();
  const people: { person: Teammate; access: string }[] = [
    { person: TEAM.daniel, access: t('Owner') },
    { person: TEAM.maya, access: t('Can view') },
    { person: TEAM.arjun, access: t('Can view') },
    { person: TEAM.lena, access: t('Can view') },
  ];

  return (
    <div className={clsxm(card, 'p-5')}>
      <p className={clsxm('text-xs font-semibold', tone.quiet)}>
        {t('Share chat')}
      </p>
      <p className={clsxm('mt-1 text-[15px] font-semibold', tone.ink)}>
        {t('Q3 pricing analysis')}
      </p>
      <div className='mt-4 flex items-center justify-between gap-3 rounded-xl bg-[#f7f7fa] px-3.5 py-3 dark:bg-white/[0.04]'>
        <span className={clsxm('text-[13px] font-medium', tone.ink)}>
          {t('Share with {{company}}', { company: COMPANY })}
        </span>
        <Toggle on />
      </div>
      <p className={clsxm('mt-4 text-xs font-semibold', tone.quiet)}>
        {t('People with access')}
      </p>
      <ul className='mt-2 space-y-2.5'>
        {people.map(({ person, access }) => (
          <li key={person.name} className='flex items-center gap-3'>
            <Avatar person={person} />
            <span
              className={clsxm(
                'min-w-0 flex-1 truncate text-[13px] font-medium',
                tone.ink,
              )}
            >
              {person.name}
            </span>
            <span className={clsxm('flex-none text-xs', tone.quiet)}>
              {access}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PromptLibrary() {
  const { t } = useB2bTranslation();
  const prompts: { title: string; author: Teammate; hidden?: boolean }[] = [
    { title: t('Weekly report from my notes'), author: TEAM.arjun },
    { title: t('Cold email opener'), author: TEAM.daniel },
    { title: t('On-brand product copy'), author: TEAM.maya, hidden: true },
    { title: t('Interview questions'), author: TEAM.lena },
  ];

  return (
    <div className={clsxm(card, 'p-5')}>
      <div className='flex items-center justify-between gap-3'>
        <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
          {t('Team prompts')}
        </p>
        <span className='rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-semibold text-primary-700 dark:bg-primary-400/15 dark:text-primary-300'>
          {t('Shared')}
        </span>
      </div>
      <ul className='mt-4 space-y-2'>
        {prompts.map(({ title, author, hidden }) => (
          <li
            key={title}
            className='rounded-xl px-3 py-2.5 ring-1 ring-[#0f0f28]/[0.07] dark:ring-white/[0.08]'
          >
            <p
              className={clsxm('truncate text-[13px] font-semibold', tone.ink)}
            >
              {title}
            </p>
            <p
              className={clsxm(
                'mt-0.5 flex items-center gap-1.5 text-xs',
                tone.quiet,
              )}
            >
              <span className='truncate'>{author.name}</span>
              {hidden ? (
                <span className='flex flex-none items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-400/10 dark:text-amber-300'>
                  <LockGlyph className='h-2.5 w-2.5' />
                  {t('Text hidden')}
                </span>
              ) : null}
            </p>
          </li>
        ))}
      </ul>
      <p className={clsxm('mt-3 text-xs leading-5', tone.quiet)}>
        {t(
          'Share a prompt and keep its text private. Teammates can still use it.',
        )}
      </p>
    </div>
  );
}

function BrandVoice() {
  const { t } = useB2bTranslation();

  return (
    <div className={clsxm(card, 'p-5')}>
      <p className={clsxm('text-xs font-semibold', tone.quiet)}>
        {t('Brand voice')}
      </p>
      <div className='mt-1 flex items-center justify-between gap-3'>
        <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
          {COMPANY}
        </p>
        <span className='flex items-center gap-2'>
          <span className={clsxm('text-xs', tone.quiet)}>
            {t('Whole team')}
          </span>
          <Toggle on />
        </span>
      </div>
      <div className='mt-4 flex flex-wrap gap-1.5'>
        {[t('Calm'), t('Expert'), t('Never pushy')].map((trait) => (
          <span
            key={trait}
            className='rounded-full bg-[#efe7dc] px-2.5 py-1 text-xs font-medium text-[#6f5134] dark:bg-[#efe7dc]/10 dark:text-[#e6cfb3]'
          >
            {trait}
          </span>
        ))}
      </div>
      <p
        className={clsxm(
          'mt-4 rounded-xl bg-[#f7f7fa] p-3.5 text-[13px] leading-6 dark:bg-white/[0.04]',
          tone.ink,
        )}
      >
        {t(
          'Our serum works while you sleep, so you wake up to calmer, brighter skin. No twelve-step routine needed.',
        )}
      </p>
      <p className={clsxm('mt-3 flex items-center gap-2 text-xs', tone.quiet)}>
        <span className='flex -space-x-1.5 rtl:space-x-reverse'>
          {[TEAM.maya, TEAM.sofia, TEAM.tom].map((person) => (
            <Avatar
              key={person.initials}
              person={person}
              size='sm'
              className='h-5 w-5 text-[8px] ring-2 ring-white dark:ring-[#16151f]'
            />
          ))}
        </span>
        {t('Used by 7 teammates')}
      </p>
    </div>
  );
}

function LockGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 20 20'
      fill='currentColor'
      aria-hidden='true'
      className={className}
    >
      <path
        fillRule='evenodd'
        d='M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z'
        clipRule='evenodd'
      />
    </svg>
  );
}
